/**
 * Date Utility Functions
 * 
 * Helper functions for parsing dates to avoid timezone-related issues.
 * 
 * PROBLEM: JavaScript interprets ISO date strings (YYYY-MM-DD) as UTC, which can 
 * shift the displayed date by a day in timezones west of Greenwich (UTC).
 * 
 * Example of the bug:
 *   - User in UTC-5 (New York)
 *   - new Date("2023-01-01") → Creates Jan 1, 2023 00:00:00 UTC
 *   - Displayed locally as: Dec 31, 2022 19:00:00 EST ← WRONG!
 * 
 * SOLUTION: Parse date components manually and create Date in local timezone.
 */

/**
 * Parse date string as local date to avoid timezone shift issues.
 * 
 * Supported formats:
 * - "YYYY-MM-DD" (ISO format - from APIs, input type="date")
 * - "YYYY-MM-DDTHH:mm:ss" (ISO with time)
 * - "MM/DD/YYYY" (US format)
 * 
 * @param dateString - Date string to parse
 * @returns Date object in local timezone, or null if invalid/empty
 * 
 * @example
 * parseAsLocalDate("2023-01-01")           // → Jan 1, 2023 00:00:00 (local)
 * parseAsLocalDate("01/01/2023")           // → Jan 1, 2023 00:00:00 (local)
 * parseAsLocalDate("2023-01-01T00:00:00Z") // → Jan 1, 2023 00:00:00 (local)
 * parseAsLocalDate(null)                   // → null
 */
export function parseAsLocalDate(dateString: string | null | undefined): Date | null {
  if (!dateString) {
    return null;
  }

  const dateStr = String(dateString).trim();
  
  if (!dateStr) {
    return null;
  }

  // Format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss (ISO format)
  // This is the problematic format that JavaScript interprets as UTC
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2].substring(0, 2), 10); // Handle "2023-01-01T00:00:00" format
    
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Format: MM/DD/YYYY (US format)
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) {
    const parts = dateStr.split('/');
    const year = parseInt(parts[2], 10);
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    
    const date = new Date(year, month, day);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Fallback: Try to parse and extract local date components
  const tempDate = new Date(dateString);
  if (!isNaN(tempDate.getTime())) {
    // Extract local date components to avoid timezone shift
    return new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
  }
  
  return null;
}

/**
 * Format any supported calendar date value as YYYY-MM-DD (for input type="date").
 * Uses string parsing only — no UTC shift for ISO date-only strings.
 */
export function toIsoDateString(value: string | Date | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return '';
    }
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmed)) {
    const parts = trimmed.split('/');
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2].substring(0, 4);
    return `${year}-${month}-${day}`;
  }

  const parsed = parseAsLocalDate(trimmed);
  if (!parsed) {
    return '';
  }
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
}

/**
 * Format any supported calendar date value as MM/DD/YYYY (API / legacy format).
 * Uses string parsing only — no UTC shift for ISO date-only strings.
 */
export function toUsDateString(value: string | Date | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }

  const trimmed = value instanceof Date ? '' : String(value).trim();
  if (trimmed && /^\d{1,2}\/\d{1,2}\/\d{4}/.test(trimmed)) {
    const parts = trimmed.split('/');
    return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2].substring(0, 4)}`;
  }

  const iso = toIsoDateString(value);
  if (!iso) {
    return '';
  }

  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
}

/**
 * Shift a YYYY-MM-DD calendar date by a number of months (local calendar arithmetic).
 */
export function shiftIsoDateMonths(isoDate: string, monthsDelta: number): string {
  const parsed = parseAsLocalDate(isoDate);
  if (!parsed) {
    return '';
  }
  parsed.setMonth(parsed.getMonth() + monthsDelta);
  return toIsoDateString(parsed);
}
